import * as z from 'zod';

import { DAY_OF_WEEK, DURATION_UNIT, MEMBERSHIP_TEMPLATE_CATEGORY } from 'src/features/membershipType/types';
import { validationMessagesMap } from 'src/utils/messages';
import { entityFormFieldSchema } from 'src/features/entity/formSchema';
import { IDENTIFIER_TYPE } from 'src/features/membership/types';
import { PERIOD_TYPE } from 'src/features/stay/types';

export type MembershipTypeFormSchemaValues = z.infer<typeof membershipTypeFormSchema>;
export const membershipTypeFormSchema = z
    .object({
        translations: z
            .array(
                z.object({
                    language: z.string().nonempty(),
                    name: z.string().nonempty(),
                }),
            )
            .min(1),
        category: z.enum(MEMBERSHIP_TEMPLATE_CATEGORY),
        site: z.string(),
        has_pricing_plan: z.boolean().nullish(),
        has_time_restriction: z.boolean().nullish(),
        pricing_plan_identity: z.string().nullish(),
        apply_pricing_per_space: z.boolean().nullish(),
        allow_custom_space_count_definition: z.boolean().nullish(),
        limit_duration: z.boolean().nullish(),
        duration_unit: z.enum(DURATION_UNIT).nullish(),
        duration: z.number().int().min(0).nullish(),
        max_allowed_spaces_count: z.number().int().nullish(),
        allow_access_over_capacity_limit: z.boolean().nullish(),
        contribute_to_available_spaces: z.boolean().nullish(),
        allow_extra_visitors: z.boolean().nullish(),
        max_extra_visitors_count: z.number().int().min(0).nullish(),
        auto_renew: z.boolean().nullish(),
        allow_access_as_client_outside_restrictions: z.boolean().nullish(),
        limit_to_roles: z.array(z.string()).nullish(),
        custom_duration: z.boolean().nullish(),
        limit_spaces: z.boolean().nullish(),
    })
    .superRefine((data, ctx) => {
        if (data.has_pricing_plan === true && !data.pricing_plan_identity) {
            ctx.addIssue({
                code: 'custom',
                path: ['pricing_plan_identity'],
                message: validationMessagesMap.validationErrorRequired,
            });
        }
        if (data.limit_duration === true && (data.custom_duration === false || data.custom_duration === undefined)) {
            if (data.duration_unit === null) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['duration_unit'],
                    message: validationMessagesMap.validationErrorRequired,
                });
            }
            if (data.duration === null) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['duration'],
                    message: validationMessagesMap.validationErrorRequired,
                });
            }
        }

        if (data.allow_extra_visitors === true && !data.max_extra_visitors_count) {
            ctx.addIssue({
                code: 'custom',
                path: ['max_extra_visitors_count'],
                message: validationMessagesMap.validationErrorRequired,
            });
        }

        if (
            data.limit_spaces === true &&
            (data.allow_custom_space_count_definition === false ||
                data.allow_custom_space_count_definition === undefined)
        ) {
            if (data.max_allowed_spaces_count === null) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['max_allowed_spaces_count'],
                    message: validationMessagesMap.validationErrorRequired,
                });
            }
        }
    });

export type MembershipIdentifierFormSchemaValues = z.infer<typeof membershipIdentifierFormSchema>;
export const membershipIdentifierFormSchema = entityFormFieldSchema.extend({
    identifier: z.object({
        value: z.string(),
        type: z.enum(IDENTIFIER_TYPE),
    }),
    public_notes: z.string().nullish(),
    private_notes: z.string().nullish(),
});

export type AllowedTimeFormSchemaValues = z.infer<typeof allowedTimeFormSchema>;
export const allowedTimeFormSchema = z.object({
    day_of_week: z.enum(DAY_OF_WEEK),
    from_time: z.string(),
    to_time: z.string(),
});

export type RestrictedGatewayFormSchemaValues = z.infer<typeof restrictedGatewayFormSchema>;
export const restrictedGatewayFormSchema = z.object({
    gatewayId: z.string(),
});

export type ZoneRuleFormSchemaValues = z.infer<typeof zoneRuleFormSchema>;
export const zoneRuleFormSchema = z
    .object({
        zone: z.string(),
        allowed_stay_type: z.enum(PERIOD_TYPE),
        transit_time_minutes: z.number().int().min(0).nullish(),
        allowed_spaces_count: z.number().int().min(0).nullish(),
        reserve_spaces_count: z.number().int().min(0).nullish(),
        extra_visitors_count: z.number().int().min(0).nullish(),
        allowed_extra_visitors_time_minutes: z.number().int().min(0).nullish(),
    })
    .transform((data) => {
        if (data.allowed_stay_type === PERIOD_TYPE.TRANSIT) {
            return {
                ...data,
                allowed_spaces_count: null,
                reserve_spaces_count: null,
                extra_visitors_count: null,
                allowed_extra_visitors_time_minutes: null,
            };
        }
        if (data.allowed_stay_type === PERIOD_TYPE.STAY) {
            return {
                ...data,
                transit_time_minutes: null,
            };
        }
        return data;
    });
